/**
 * Real-time Notifications Hook
 *
 * Subscribes to user's notification channel and invalidates TanStack Query cache
 * when new notifications arrive.
 */

import { useCallback } from 'react';
import { useQueryClient, QueryKey } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { useAppSelector } from '@/store/store';
import { selectCurrentUserId } from '@/store/slices/authSlice';
import { socialKeys } from '@/api/keys/socialKeys';
import { useAblyChannel } from './useAblyChannel';
import { useIsRealtimeActive } from './AblyProvider';
import { usePendingRefetch } from './usePendingRefetch';

interface NotificationSignal {
  type: 'notification';
  notificationType: string;
  notificationId: number;
  timestamp: number;
  hint?: {
    action?: string;
    senderName?: string;
    notificationType?: string;
  };
}

interface UseRealtimeNotificationsReturn {
  /** Whether real-time notifications are active */
  isRealtimeActive: boolean;
}

/**
 * Subscribe to real-time notification signals
 *
 * When a signal is received, it invalidates the notification queries
 * to trigger a refetch from the REST API.
 *
 * @example
 * ```tsx
 * const { isRealtimeActive } = useRealtimeNotifications();
 *
 * // Use with polling fallback
 * const { data } = useGetUnreadCount({
 *   refetchInterval: isRealtimeActive ? false : 30000,
 * });
 * ```
 */
export function useRealtimeNotifications(): UseRealtimeNotificationsReturn {
  const queryClient = useQueryClient();
  const userId = useAppSelector(selectCurrentUserId);
  const isRealtimeActive = useIsRealtimeActive();
  const { trackInvalidation } = usePendingRefetch();
  const toast = useToast();

  // Helper to track and invalidate a query (tracks for background refetch)
  const invalidateWithTracking = useCallback(
    (queryKey: QueryKey) => {
      trackInvalidation(queryKey);
      queryClient.invalidateQueries({ queryKey });
    },
    [queryClient, trackInvalidation]
  );

  const handleNotificationSignal = useCallback(
    (message: { data: NotificationSignal }) => {
      console.log('[Realtime] Notification signal received:', message.data);
      const { notificationType } = message.data;

      // Invalidate notification queries to trigger refetch (with background tracking)
      invalidateWithTracking(socialKeys.unreadCount());
      invalidateWithTracking(socialKeys.notificationsList());

      // Show global toast for time-sensitive notifications
      if (notificationType === 'collab_round_ending') {
        toast({
          title: 'Survivor Round Ending Soon!',
          description: 'Cast your elimination votes before time runs out.',
          status: 'warning',
          duration: 10000,
          isClosable: true,
        });
      }
    },
    [invalidateWithTracking, toast]
  );

  useAblyChannel<NotificationSignal>({
    channelName: userId ? `user-${userId}-notifications` : '',
    eventName: 'signal',
    onMessage: handleNotificationSignal,
    enabled: !!userId && isRealtimeActive,
  });

  return {
    isRealtimeActive,
  };
}
